import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import P from '../../Common/Components/P'
import FormRow from '../../Common/Components/FormRow'
import TextInput from '../../Common/Components/TextInput'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ContentRestriction as ContentRestrictionLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'

/**
 * Component properties type.
 */
type Props = {
    isWallboardReport: boolean
    maxFilterListLimit: number | undefined
    setMaxFilterListLimit: (newValue: number | undefined) => void
    maxRptRowDisplLimit: number | undefined
    setMaxRptRowDisplLimit: (newValue: number | undefined) => void
    initialiseRptFilterEntityInfo: () => Promise<void>
}

/**
 * Content restriction section in Advanced tab of report configuration modal window.
 *
 * @param isWallboardReport Flag specifying whether current report is a Dashboard.
 * @param maxFilterListLimit Report property.
 * @param setMaxFilterListLimit Setter for corresponding report property.
 * @param maxRptRowDisplLimit Report property.
 * @param setMaxRptRowDisplLimit Setter for corresponding report property.
 * @param initialiseRptFilterEntityInfo Initialisation method for filter entity data.
 */
const ContentRestriction: React.FC<Props> = ({
    isWallboardReport,
    maxFilterListLimit,
    setMaxFilterListLimit,
    maxRptRowDisplLimit,
    setMaxRptRowDisplLimit,
    initialiseRptFilterEntityInfo
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('ContentRestriction')

    return (
        <Fieldset className={'content-restriction'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(ContentRestrictionLanguage.ContentRestriction_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_CONTENT_RESTRICTION}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>
            <P>{language.get(ContentRestrictionLanguage.ContentRestriction_SectionPara)}</P>
            <FormRow isInline className={'max-filter-list-limit'}>
                <TextInput
                    value={maxFilterListLimit}
                    onChange={(value: string): void => setMaxFilterListLimit(parseInt(value, 10) || undefined)} // TO DO: add validation
                    labelLeft={language.get(ContentRestrictionLanguage.MaxfilterlistlimitLabel)}
                    placeholder={language.get(ContentRestrictionLanguage.MaxfilterlistlimitPlaceholder)}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onBlur={initialiseRptFilterEntityInfo}
                />
            </FormRow>
            {!isWallboardReport && (
                <FormRow isInline className={'max-report-row-display-limit'}>
                    <TextInput
                        value={maxRptRowDisplLimit}
                        onChange={(value: string): void => setMaxRptRowDisplLimit(parseInt(value, 10) || undefined)}
                        labelLeft={language.get(ContentRestrictionLanguage.MaxreportrowdisplimitLabel)}
                        placeholder={language.get(ContentRestrictionLanguage.MaxreportrowdisplimitPlaceholder)}
                    />
                </FormRow>
            )}
        </Fieldset>
    )
}

export default React.memo(ContentRestriction)
